import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { getProfile, saveProfile } from "@/lib/profile";
import { FEELINGS, feelingLabel, feelingIcon } from "@/lib/feelings";
import { armOpeningSound } from "@/lib/openingSound";
import { startWaves, stopWaves } from "@/lib/ambiance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DURATIONS = [5, 10, 20, 30];
const DEFAULT_NEEDS = ["Me reposer / préparer mon sommeil", "Retrouver un peu d'énergie", "Relâcher mon corps", "Calmer mon mental"];
const SURPRISE = "Je ne sais pas, surprenez-moi";

const EASE = [0.22, 1, 0.36, 1];

const sectionReveal = {
  // Apparition au chargement (pas au défilement) : fiable sur tous les téléphones
  initial: { opacity: 0, y: 26 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: EASE },
};

export default function Welcome() {
  const navigate = useNavigate();
  const [gender, setGender] = useState("fille"); // défaut : clientèle majoritairement féminine — sert aux dessins
  const [need, setNeed] = useState(null);
  const [besoin, setBesoin] = useState(null);
  const [minutes, setMinutes] = useState(null);
  const [needsByFeeling, setNeedsByFeeling] = useState({});
  const [searchParams] = useSearchParams();
  const editing = searchParams.get("edit") === "1";

  // Première visite uniquement : si le profil existe déjà, on entre directement dans l'app
  useEffect(() => {
    armOpeningSound();
    const saved = getProfile();
    if (saved && !editing) {
      navigate("/accueil", { replace: true });
      return;
    }
    if (saved?.gender) setGender(saved.gender);
  }, [navigate, editing]);

  const effectiveGender = gender || "fille";

  // Vagues en fond pendant le choix — fondu de sortie en quittant la page
  useEffect(() => {
    startWaves();
    return () => stopWaves();
  }, []);

  // Chaque choix est enregistré immédiatement — transmis à l'accueil
  useEffect(() => {
    localStorage.setItem("rav-instant", JSON.stringify({ need, minutes, besoin }));
  }, [need, minutes, besoin]);

  // Besoins proposés par ressenti (éditables en admin, module Ressentis & besoins)
  useEffect(() => {
    axios
      .get(`${API}/feelings/public`)
      .then(({ data }) => {
        const map = {};
        (data.feelings || []).forEach((f) => {
          map[f.name] = f.needs || [];
        });
        setNeedsByFeeling(map);
      })
      .catch(() => {});
  }, []);

  const validate = () => {
    // Seuls le ressenti, le besoin et la durée comptent ici — prénom et date de
    // naissance seront demandés à la création du compte (page suivante du parcours)
    if (!need) {
      toast("Dites-nous comment vous vous sentez aujourd'hui.");
      return;
    }
    if (!minutes) {
      toast("Choisissez le temps dont vous disposez.");
      return;
    }
    saveProfile({ gender: effectiveGender }); // le sexe sert aux illustrations
    // L'Instant est composé TOUT DE SUITE — aucune question reposée deux fois
    localStorage.setItem("rav-instant", JSON.stringify({ need, minutes, besoin }));
    // Non inscrite : la page de création de compte (page unique : identité, e-mail,
    // mot de passe, formule, essai) vient AVANT la découverte de l'Instant
    if (!localStorage.getItem("rav-token")) {
      navigate("/connexion?mode=register", { replace: true });
      return;
    }
    navigate("/instant", { replace: true });
  };

  return (
    <>
      <header className="rav-hero">
        <span className="rav-branch rav-branch-left" aria-hidden="true" />
        <span className="rav-branch rav-branch-right" aria-hidden="true" />
        <motion.div
          className="rav-emblem-wrap"
          data-testid="welcome-emblem"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE }}
        >
          <div className="rav-halo" />
          <img
            src="/logo-accueil.webp"
            alt="Respire Aime Vibre — Julie Pomares Durand, Sophrologue"
            className="rav-logo-img"
            draggable="false"
          />
        </motion.div>
        <motion.h1
          className="rav-tagline"
          data-testid="welcome-tagline"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
        >
          L’Instant qui vous ressemble
        </motion.h1>
        <motion.div
          className="rav-divider"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: EASE }}
        >
          <img src="/divider-heart.webp" alt="" aria-hidden="true" className="rav-divider-img" />
        </motion.div>
      </header>

      <main className="rav-content" data-testid="welcome-page">
        {/* Seule info demandée ici : femme ou homme — pour afficher les bons dessins.
            Prénom et date de naissance sont demandés plus tard, à la création du compte */}
        <motion.section
          className="rav-step rav-welcome-fields"
          data-testid="welcome-step-gender"
          {...sectionReveal}
        >
          <div className="rav-field" data-testid="welcome-gender-block">
            <span>Vous êtes :</span>
            <div className="rav-chips">
              {[["fille", "Une femme"], ["garçon", "Un homme"]].map(([g, label]) => (
                <button
                  key={g}
                  type="button"
                  className={`rav-chip${effectiveGender === g ? " is-selected" : ""}`}
                  data-testid={`welcome-gender-${g}`}
                  aria-pressed={effectiveGender === g}
                  onClick={() => setGender(g)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </motion.section>

        <motion.section className="rav-step" data-testid="welcome-step-need" {...sectionReveal} transition={{ ...sectionReveal.transition, delay: 0.06 }}>
          <div className="rav-step-head">
            <span className="rav-step-num">1</span>
            <h2 className="rav-step-title rav-step-title--upright">Comment vous sentez-vous aujourd’hui ?</h2>
          </div>
          <div className="rav-feel-grid">
            {FEELINGS.map((feeling) => (
              <button
                key={feeling.f}
                type="button"
                data-testid={`welcome-feeling-${feeling.f}`}
                className={`rav-feel-card${need === feeling.f ? " is-selected" : ""}${feeling.f === "Je ne sais pas vraiment" ? " rav-feel-card--wide" : ""}`}
                aria-pressed={need === feeling.f}
                onClick={() => {
                  setNeed(feeling.f);
                  setBesoin(null);
                }}
              >
                <img
                  src={feelingIcon(feeling, effectiveGender)}
                  alt=""
                  aria-hidden="true"
                  className="rav-feel-icon"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    if (e.currentTarget.src !== window.location.origin + feeling.icon) e.currentTarget.src = feeling.icon;
                  }}
                />
                <span>{feelingLabel(feeling, effectiveGender)}</span>
              </button>
            ))}
          </div>
        </motion.section>

        {need && need !== "Je ne sais pas vraiment" && (
          <motion.section
            className="rav-step"
            data-testid="welcome-step-besoin"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <div className="rav-step-head">
              <span className="rav-step-num">2</span>
              <h2 className="rav-step-title">De quoi avez-vous besoin maintenant ?</h2>
            </div>
            <div className="rav-pills rav-needs-pills">
              {(needsByFeeling[need]?.length ? needsByFeeling[need] : DEFAULT_NEEDS).slice(0, 4).map((n, i) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`welcome-besoin-${i}`}
                  className={`rav-pill rav-need-pill${besoin === n ? " is-selected" : ""}`}
                  aria-pressed={besoin === n}
                  onClick={() => setBesoin(n)}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                data-testid="welcome-besoin-surprise"
                className={`rav-pill rav-need-pill rav-need-surprise${besoin === SURPRISE ? " is-selected" : ""}`}
                aria-pressed={besoin === SURPRISE}
                onClick={() => setBesoin(SURPRISE)}
              >
                <Sparkle size={16} strokeWidth={1.6} />
                {SURPRISE}
              </button>
            </div>
          </motion.section>
        )}

        <motion.section
          className="rav-step"
          data-testid="welcome-step-duration"
          {...sectionReveal}
          transition={{ ...sectionReveal.transition, delay: 0.08 }}
        >
          <div className="rav-step-head">
            <span className="rav-step-num">{need && need !== "Je ne sais pas vraiment" ? "3" : "2"}</span>
            <h2 className="rav-step-title">Combien de temps avez-vous ?</h2>
          </div>
          <div className="rav-pills">
            {DURATIONS.map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`welcome-duration-${m}`}
                className={`rav-pill${minutes === m ? " is-selected" : ""}`}
                aria-pressed={minutes === m}
                onClick={() => setMinutes(m)}
              >
                <Clock size={19} strokeWidth={1.6} />
                {m} min
              </button>
            ))}
          </div>
        </motion.section>

        <motion.div {...sectionReveal} transition={{ ...sectionReveal.transition, delay: 0.2 }}>
          <motion.button
            type="button"
            whileTap={{ scale: 0.98 }}
            className="rav-primary-button"
            data-testid="welcome-validate-button"
            onClick={validate}
          >
            <Sparkle size={16} strokeWidth={1.6} />
            Je crée mon Instant
          </motion.button>
          <p className="rav-page-sub" data-testid="welcome-trial-note" style={{ textAlign: "center", marginTop: 10 }}>
            1 semaine offerte — 7 Instants à créer librement, sans carte bancaire
          </p>
          <button
            type="button"
            className="rav-text-link rav-link-strong"
            data-testid="welcome-to-login"
            onClick={() => navigate("/connexion?mode=login")}
          >
            Me connecter à mon Espace
          </button>
        </motion.div>
      </main>
    </>
  );
}
